/**
 * Utility for persisting filter, search, sorting, and pagination states across module navigation.
 * Uses sessionStorage so states persist during tab sessions but reset cleanly on new sessions.
 */

const STORAGE_PREFIX = 'kqspl_filter_v1_';

/**
 * Retrieve saved filter parameters and pagination info for a module.
 */
export const getSavedFilterState = (key, defaultParams = {}, defaultPageSize = 50) => {
    if (!key) return { params: defaultParams, page: 1, pageSize: defaultPageSize };
    try {
        const raw = sessionStorage.getItem(`${STORAGE_PREFIX}${key}`);
        if (raw) {
            const data = JSON.parse(raw);
            return {
                params: { ...defaultParams, ...(data.params || {}) },
                page: typeof data.page === 'number' ? data.page : 1,
                pageSize: typeof data.pageSize === 'number' ? data.pageSize : defaultPageSize,
            };
        }
    } catch (err) {
        console.warn(`Error reading saved filter state for key "${key}":`, err);
    }
    return { params: defaultParams, page: 1, pageSize: defaultPageSize };
};

/**
 * Save current filter parameters and pagination info for a module.
 */
export const saveFilterState = (key, params, page = 1, pageSize = 50) => {
    if (!key) return;
    try {
        sessionStorage.setItem(`${STORAGE_PREFIX}${key}`, JSON.stringify({
            params: params || {},
            page: page || 1,
            pageSize: pageSize || 50,
            updatedAt: Date.now(),
        }));
    } catch (err) {
        console.warn(`Error saving filter state for key "${key}":`, err);
    }
};

/**
 * Clear saved filter state for a module.
 */
export const clearSavedFilterState = (key) => {
    if (!key) return;
    try {
        sessionStorage.removeItem(`${STORAGE_PREFIX}${key}`);
    } catch (err) {
        console.warn(`Error clearing filter state for key "${key}":`, err);
    }
};

/**
 * Clear ALL saved filter states across all modules (e.g. on user logout).
 */
export const clearAllSavedFilterStates = () => {
    try {
        if (typeof sessionStorage !== 'undefined') {
            const keysToRemove = [];
            for (let i = 0; i < sessionStorage.length; i++) {
                const key = sessionStorage.key(i);
                if (key && key.startsWith(STORAGE_PREFIX)) {
                    keysToRemove.push(key);
                }
            }
            keysToRemove.forEach(key => sessionStorage.removeItem(key));
        }
    } catch (err) {
        console.warn('Error clearing all saved filter states:', err);
    }
};

/**
 * Check whether a set of filter parameters contains non-default active filters.
 */
export const hasActiveFilters = (filters, defaultFilters = {}) => {
    if (!filters) return false;
    return Object.keys(filters).some(key => {
        if (key === 'ordering' || key === 'page' || key === 'page_size') return false;
        const val = filters[key];
        const def = defaultFilters[key];
        if (val === undefined || val === null || val === '') return false;
        return val !== def;
    });
};
